<template>
  <div class="location-history">
    <div class="locations-list" v-if="locationMode === 'history'">
      <div v-if="loading" class="loader">
        <base-circular-loader color="primary"
                              size="small" />
      </div>

      <ul v-else class="list">
        <li v-for="(location, index) in filteredLocations"
            :key="index"
            :class="{ selected: isActive(index) }"
            class="location"
            @click="onLocationClick(location, index)">
          <location-item :location="location"
                         :child-id="childId"
                         :head="index === 0" />
        </li>

        <pagination-trigger v-if="showPaginationTrigger"
                            @triggerIntersected="loadMore" />
      </ul>
    </div>

    <div class="location-mode">
      <div class="mode-name mode-name-now">
        {{ $t("tabs.location.locationNow") }}
      </div>

      <div class="mode-switch">
        <base-toggle
          :value="locationMode"
          true-value="history"
          false-value="now"
          @change="changeLocationMode"
        />
      </div>

      <div class="mode-name mode-name-history">
        {{ $t("tabs.location.locationHistory") }}
      </div>
    </div>
  </div>
</template>

<script>
  import LocationItem from '@/components/Child/ChildTabs/TabLocation/LocationHistoryItem.vue'
  import PaginationTrigger from '@/components/Common/PaginationTrigger'

  export default {
    components: {
      LocationItem,
      PaginationTrigger
    },

    props: {
      primaryDeviceId: {
        type: String,
        required: true
      },

      device: {
        type: Object,
        required: false
      },

      childId: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        loading: false,
        locationMode: 'now',
        showPaginationTrigger: true,
        selectedItemIndex: null,
        pageSize: 20
      }
    },

    created() {
      this.clearLocationHistory()
      this.loadNamedLocations(this.currentUser.group_id)
    },

    methods: {
      ...mapActions('geofence', {
        getLocationHistory: 'getLocationHistory',
        clearLocationHistory: 'clearLocationHistory',
        loadNamedLocations: 'loadNamedLocations'
      }),

      getLocations() {
        this.$store.dispatch('geofence/getLocationHistory', {
          user_id: this.childId,
          page_size: this.pageSize
        })
          .then(data => {
            this.locations = data
          })
      },

      loadMore() {
        this.getLocationHistory({
          user_id: this.childId,
          page_size: this.pageSize,
          max_date: this.oldestLoadedLocation.track_date
        }).then(locations => {
            if (locations.length < this.pageSize) {
              this.showPaginationTrigger = false
            }
          })

      },

      changeLocationMode(e) {
        this.locationMode = e
        this.$emit('modeChange', e)

        if (this.locationMode === 'now') {
          this.clearLocationHistory()

          return
        }

        this.loading = true

        this.getLocationHistory({
        user_id: this.childId,
        page_size: this.pageSize
        }).then(locations => {
          this.loading = false
          if (locations.length < this.pageSize) {
            this.showPaginationTrigger = false
          }
        })
      },

      onLocationClick(location, index) {
        this.selectedItemIndex = index
        this.$emit('locationChange', {
          ...this.child,
          latest_location: {
            ...location,
            from_history: true
          }
        })
      },

      isActive(index) {
        return this.selectedItemIndex === index
      }
    },

    computed: {
      ...mapGetters('geofence', {
        namedLocations: 'getNamedLocations',
        locations: 'getLocationHistory'
      }),

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      }),

      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      child() {
        return this.getChildById(this.childId)
      },

      oldestLoadedLocation() {
        return this.locations[this.locations.length - 1]
      },

      namedLocationsIds() {
        return this.namedLocations.map(location => location.id)
      },

      filteredLocations() {
        return this.locations.filter(location => {
          return location.address || this.namedLocationsIds.includes(location.named_location_id)
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .location-history {
    background-color: transparent;
    width: 440px;
    height: auto;

    .locations-list {
      width: 440px;
      height: 195px;
      max-height: 195px;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

      .loader {
        width: 100%;
        height: 100%;

        display: flex;
        justify-content: center;
        align-items: center;
      }

      .list {
        list-style-type: none;
        height: 195px;
        overflow: auto;
        padding: 0;
        margin: 0;

        .location {
          cursor: pointer;
          width: 100%;
          height: auto;

          display: flex;
          justify-content: space-between;
          align-items: center;

          background: #fff;
          box-sizing: border-box;
          border-bottom: 1px solid #f2f2f2;
          opacity: .7;

          &:first-child {
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          }

          &:last-child{
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
          }

          &:nth-child(2n+1):nth-last-child(-n+2),
          &:nth-child(2n+1):nth-last-child(-n+2) ~ li {
            border:none;
          }

          &:hover {
            opacity: 1;
            background: color.adjust(map.get($colors, primary), $lightness: 40%);

            .address,
            .time {
              opacity: 1;
            }
          }
        }

        .selected {
          background:  color.adjust(map.get($colors, primary), $lightness: 40%);

          .address,
          .time {
            opacity: 1;
          }
        }
      }
    }

    .location-mode {
      height: 50px;
      width: 440px;
      border-radius: 8px;
      padding: 0 16px 0 16px;
      margin: 11px 0 0 0;
      background: #fff;
      display: flex;
      justify-content: space-between;

      .mode-switch {
        width: 51px;
        margin: 12px;
      }

      .mode-name {
        cursor: default;
        height: 19px;
        color: #3e3a3b;
        font-size: 16px;
        margin: 17px 0 15px 0;

        &-now {
          width: 100px;
        }

        &-history {
          width: 118px;
        }
      }
    }
  }
</style>
